import { Div } from '@codesuma/baseline'
import listsService, { UserList } from '../../services/lists'
import helpers from '../../helpers'
import { iconForName } from './icon-for-name'
import { pickedIconUrl } from '../../configs/list-icons'
import style from './index.module.css'

// A list rendered as a colored circle. On the home page it can `expand` to fill
// the screen (morphing into the list page) or `contract` back into place.
export const ListCircle = (item: UserList) => {
    const base = Div()
    base.addClass(style.base)
    // Colors are derived from the id, so a list keeps the same circle background
    // and text color everywhere. On night the fill is translucent so the sky shows through.
    base.style({ backgroundColor: helpers.surfaceForId(item.id), color: helpers.textForId(item.id) })

    const label = Div(item.name)
    label.addClass(style.label)

    // Progress badge pinned to the top of the circle: "done/total" in a light font.
    // Shown whenever we know the total; done_count defaults to 0 for lists that
    // don't carry it yet (just-created or server lists) and self-corrects once the
    // list is opened (list.ts recomputes it), so every circle gets a counter.
    let count: ReturnType<typeof Div> | null = null
    if (typeof item.item_count === 'number') {
        count = Div((item.done_count ?? 0) + '/' + item.item_count)
        count.addClass(style.count)
        // base.append(count)
    }

    const meta = Div()
    meta.addClass(style.meta)
    if (item.role === 'member' && item.shared_by) {
        meta.text('from ' + item.shared_by)
    } else if (typeof item.item_count === 'number') {
        meta.text(item.item_count + (item.item_count === 1 ? ' item' : ' items'))
    } else if (item.role === 'member') {
        meta.text('shared')
    }

    // Above the title: the icon picked in the New List wizard, or else one
    // guessed from the list's name. A CSS mask tinted with the circle's text
    // color, like the pin and share badge.
    const icon = Div()
    icon.addClass(style.icon)
    const paintIcon = () => icon.el.style.setProperty('--icon', `url(${pickedIconUrl(item.icon) ?? iconForName(item.name)})`)
    paintIcon()

    base.append(icon, label)

    // Badge only on shared lists (either direction): a share icon, tinted via CSS
    // mask so it picks up the circle's dark text color. Private lists get no
    // badge, so the label sits centered and uncluttered.
    const state = helpers.shareState(item.role, item.member_count)
    let badge: ReturnType<typeof Div> | null = null
    if (state !== 'private') {
        badge = Div()
        badge.addClass(style.badge)
        const badgeIcon = Div()
        badgeIcon.addClass(style.badgeIcon)
        badge.append(badgeIcon)
        base.append(badge)
    }

    // Pinned marker at the top of the circle. Pinning is a per-device choice
    // (lists service localStorage), so it's read here, not carried on the row;
    // the home page rebuilds circles when it changes (pin state is in its sig).
    const pinned = listsService.isPinned(item.id)
    let pin: ReturnType<typeof Div> | null = null
    if (pinned) {
        pin = Div()
        pin.addClass(style.pin)
        base.append(pin)
    }

    // Tactile press feedback: dip the circle down while held, spring it back on
    // release. Uses a quick inline transition; cleared back to the CSS default
    // (the slower morph) by expand()/contract().
    const press = (down: boolean) => {
        base.style({
            transition: down ? 'transform 0.12s ease' : 'transform 0.2s ease',
            transform: `translate(0px, 0px) scale(${down ? 0.92 : 1})`,
        })
    }

    // Moving the pointer (scrolling) cancels the press dip so a scroll doesn't
    // leave the circle stuck down.
    let startX = 0
    let startY = 0

    base.el.addEventListener('pointerdown', (e: PointerEvent) => {
        startX = e.clientX
        startY = e.clientY
        press(true)
    })
    base.el.addEventListener('pointermove', (e: PointerEvent) => {
        if (Math.abs(e.clientX - startX) > 8 || Math.abs(e.clientY - startY) > 8) {
            press(false)
        }
    })
    base.el.addEventListener('pointerup', () => press(false))
    base.el.addEventListener('pointercancel', () => press(false))
    base.el.oncontextmenu = (e: Event) => e.preventDefault()

    base.el.onclick = () => base.emit('circle-open', item)

    return Object.assign(base, {
        item,
        // Pin state this circle was built with — the home page compares it to
        // the live value to know a rebuild (reorder) is needed.
        pinned,

        // Repaint the displayed counts and icon after an edit on the list page
        // (the home page calls this when returning to an existing circle). Keeps
        // the stored item in sync too, so a later full render stays correct.
        update(next: UserList) {
            Object.assign(item, next)
            paintIcon()
            if (count && typeof next.item_count === 'number') {
                count.text((next.done_count ?? 0) + '/' + next.item_count)
            }
            if (next.role !== 'member' && typeof next.item_count === 'number') {
                meta.text(next.item_count + (next.item_count === 1 ? ' item' : ' items'))
            }
        },

        // Re-read the palette (e.g. after a theme flip) and re-apply this list's
        // colors in place, without rebuilding the circle.
        recolor() {
            base.style({ backgroundColor: helpers.surfaceForId(item.id), color: helpers.textForId(item.id) })
        },

        // Reset to the resting circle state (used when returning to the home page).
        contract() {
            base.style({
                transition: '', // back to the CSS morph timing (drop any press transition)
                transform: 'translate(0px, 0px) scale(1)',
                opacity: '1',
                zIndex: '1',
                pointerEvents: 'auto',
            })
            label.style({ opacity: '1' })
            meta.style({ opacity: '1' })
            count?.style({ opacity: '' }) // back to the CSS resting opacity
            badge?.style({ opacity: '.5' })
            pin?.style({ opacity: '' })
            icon.style({ opacity: '' })
        },

        // x/y is the screen-space center of the tapped circle.
        // The tapped circle grows to cover the viewport; the rest scatter away.
        expand(x: number, y: number, id: string) {
            const w = base.el.getBoundingClientRect().width
            if (id === item.id) {
                // Content fades out fast (quick CSS opacity transition) while the
                // circle grows straight away — no pause after the press dip.
                label.style({ opacity: '0' })
                meta.style({ opacity: '0' })
                count?.style({ opacity: '0' })
                badge?.style({ opacity: '0' })
                pin?.style({ opacity: '0' })
                icon.style({ opacity: '0' })
                const scale = 1.5 * Math.max(window.innerWidth, window.innerHeight) / w
                const dx = window.innerWidth / 2 - x
                const dy = window.innerHeight / 2 - y
                base.style({ transition: '', zIndex: '5' }) // restore the CSS morph timing
                base.style({
                    transform: `translate(${dx}px, ${dy}px) scale(${scale})`,
                    pointerEvents: 'none',
                }, 20)
                return
            }

            const rect = base.el.getBoundingClientRect()
            const cx = rect.left + rect.width / 2
            const cy = rect.top + rect.height / 2
            const max = 260
            const tx = cx > x ? max : cx < x ? -max : 0
            const ty = cy > y ? max : cy < y ? -max : 0
            base.style({
                opacity: '0',
                transform: `translate(${tx}px, ${ty}px) scale(0.4)`,
            })
        },
    })
}
